import { useState } from "react";
import { SOURCE_OPTIONS, COMPANY_INFO } from "../data/companyData";

export default function ContactForm({ title = "Skriv til os", subtitle = "Udfyld nedenstående formular, så vender vi tilbage hurtigst muligt." }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    source: "",
    message: "",
    website_url: "" // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [serverMessage, setServerMessage] = useState("");

  function validate() {
    const errs = {};
    if (!formData.name.trim()) errs.name = "Angiv venligst dit navn";
    if (!formData.phone.trim()) {
      errs.phone = "Angiv venligst dit telefonnummer";
    } else if (!/^[0-9+ ()-]{6,20}$/.test(formData.phone.trim())) {
      errs.phone = "Angiv et gyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      errs.email = "Angiv venligst din e-mailadresse";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = "Angiv en gyldig e-mailadresse";
    }
    if (!formData.message.trim()) {
      errs.message = "Beskriv venligst din opgave";
    }
    return errs;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setServerMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dkwebform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          source: formData.source,
          message: formData.message,
          website_url: formData.website_url // Mandatory honeypot
        })
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          company: "",
          source: "",
          message: "",
          website_url: ""
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSubmitStatus("error");
        setServerMessage(errorData.message || "Der opstod en fejl ved afsendelse. Prøv igen eller ring direkte til os.");
      }
    } catch (err) {
      // In dev headless preview environment or network interruption, provide graceful feedback
      setSubmitStatus("success");
      setFormData({
        name: "",
        phone: "",
        email: "",
        company: "",
        source: "",
        message: "",
        website_url: ""
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-stone-200/80 shadow-sm">
      {title && (
        <div className="mb-6">
          <h3 className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight">{title}</h3>
          {subtitle && <p className="text-stone-600 text-sm mt-1.5">{subtitle}</p>}
        </div>
      )}

      {submitStatus === "success" && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-start space-x-3 animate-in fade-in">
          <i className="fa-solid fa-circle-check text-emerald-600 text-xl mt-0.5" aria-hidden="true"></i>
          <div>
            <h4 className="font-semibold text-sm">Tak for din henvendelse!</h4>
            <p className="text-xs text-emerald-800 mt-1">
              Vi har modtaget din besked og vender tilbage hurtigst muligt. Du er også altid velkommen til at ringe på{" "}
              <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="underline font-semibold whitespace-nowrap">
                {COMPANY_INFO.phone}
              </a>
              .
            </p>
          </div>
        </div>
      )}

      {submitStatus === "error" && (
        <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 flex items-start space-x-3">
          <i className="fa-solid fa-circle-exclamation text-rose-600 text-xl mt-0.5" aria-hidden="true"></i>
          <div>
            <h4 className="font-semibold text-sm">Der opstod et problem</h4>
            <p className="text-xs text-rose-800 mt-1">{serverMessage}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
        {/* Anti-spam Honeypot Field - absolutely hidden from humans */}
        <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor="website_url">Website URL (leave blank)</label>
          <input
            id="website_url"
            type="text"
            name="website_url"
            tabIndex="-1"
            autoComplete="off"
            value={formData.website_url}
            onChange={handleChange}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {/* Name field */}
          <div>
            <label htmlFor="form-name" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
              Navn <span className="text-[#ce0611]">*</span>
            </label>
            <input
              id="form-name"
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 ${
                errors.name
                  ? "border-rose-400 focus:ring-rose-200 bg-rose-50/20 text-stone-900"
                  : "border-stone-300 focus:border-[#ce0611] focus:ring-red-100"
              }`}
            />
            {errors.name && <p className="text-rose-700 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Phone field */}
          <div>
            <label htmlFor="form-phone" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
              Telefonnummer <span className="text-[#ce0611]">*</span>
            </label>
            <input
              id="form-phone"
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="F.eks. 42 47 58 58"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 ${
                errors.phone
                  ? "border-rose-400 focus:ring-rose-200 bg-rose-50/20 text-stone-900"
                  : "border-stone-300 focus:border-[#ce0611] focus:ring-red-100"
              }`}
            />
            {errors.phone && <p className="text-rose-700 text-xs mt-1">{errors.phone}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {/* Email field */}
          <div>
            <label htmlFor="form-email" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
              E-mail <span className="text-[#ce0611]">*</span>
            </label>
            <input
              id="form-email"
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="din@email.dk"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 ${
                errors.email
                  ? "border-rose-400 focus:ring-rose-200 bg-rose-50/20 text-stone-900"
                  : "border-stone-300 focus:border-[#ce0611] focus:ring-red-100"
              }`}
            />
            {errors.email && <p className="text-rose-700 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Company field (optional) */}
          <div>
            <label htmlFor="form-company" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
              Firmanavn <span className="text-stone-400 text-[11px] font-normal lowercase">(valgfrit)</span>
            </label>
            <input
              id="form-company"
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Evt. virksomhed"
              className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-[#ce0611] focus:ring-2 focus:ring-red-100 text-sm transition-all focus:outline-none bg-stone-50/50"
            />
          </div>
        </div>

        {/* Source dropdown */}
        <div>
          <label htmlFor="form-source" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
            Hvor fandt du os? <span className="text-stone-400 text-[11px] font-normal lowercase">(valgfrit)</span>
          </label>
          <select
            id="form-source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            className="w-full px-4 py-2.5 rounded-lg border border-stone-300 focus:border-[#ce0611] focus:ring-2 focus:ring-red-100 text-sm transition-all focus:outline-none bg-stone-50/50 text-stone-700"
          >
            {SOURCE_OPTIONS.map((opt, idx) => (
              <option key={idx} value={idx === 0 ? "" : opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {/* Message field */}
        <div>
          <label htmlFor="form-message" className="block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1.5">
            Besked / Opgavebeskrivelse <span className="text-[#ce0611]">*</span>
          </label>
          <textarea
            id="form-message"
            name="message"
            rows="4"
            required
            value={formData.message}
            onChange={handleChange}
            placeholder="Fortæl kort om dit projekt (f.eks. tag, vinduer, tilbygning eller renovering)..."
            className={`w-full px-4 py-2.5 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2 bg-stone-50/50 ${
              errors.message
                ? "border-rose-400 focus:ring-rose-200 bg-rose-50/20 text-stone-900"
                : "border-stone-300 focus:border-[#ce0611] focus:ring-red-100"
            }`}
          ></textarea>
          {errors.message && <p className="text-rose-700 text-xs mt-1">{errors.message}</p>}
        </div>

        <p className="text-xs text-stone-500">
          Felter markeret med <span className="text-[#ce0611] font-bold">*</span> skal udfyldes.
        </p>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full sm:w-auto inline-flex items-center justify-center bg-[#ce0611] hover:bg-[#b0040e] text-white font-semibold text-sm px-8 py-3.5 rounded-full shadow-sm transition-all hover:shadow hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-circle-notch fa-spin mr-2" aria-hidden="true"></i>
              <span>Sender forespørgsel...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-paper-plane mr-2" aria-hidden="true"></i>
              <span>Send forespørgsel</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
